import React, { useState, useRef } from 'react';

const CategoryDropdown = ({ categories, handleCategoryClick, onClose }) => {
  const [clickedCategoryId, setClickedCategoryId] = useState({});
  const [subcategoriesData, setSubcategoriesData] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [touchData, setTouchData] = useState({});
  const dropdownRef = useRef(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const getCategoryImage = (category) => {
    const categoryName = category.acMainCategory || category.name;
    const images = {
      'Display, Televizori, Projektori, Audio': 'https://resource.ewe.rs/media/category/2022/07/Televizori-projektori-audio.png',
      'Klima uređaji i grejna tela': 'https://resource.ewe.rs/media/category/2022/07/Klima-uredjaji-i-grejna-tela.png',
      'Kućni aparati i posuđe': 'https://resource.ewe.rs/media/category/2022/07/Kucni-kuhinjski-aparati-i-posudje.png',
      'Mobilni telefoni, tableti i oprema': 'https://resource.ewe.rs/media/category/2020/07/Mobilni telefoni i oprema.png',
      'Štampači, skeneri i potrošni': 'https://resource.ewe.rs/products/PRI05551_v.jpg',
      'Outlet': 'https://cdn-icons-png.flaticon.com/512/1057/1057323.png'
    };

    if (images[categoryName]) return images[categoryName];

    const sanitizedName = categoryName
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/(^-+|-+$)/g, '');

    return `https://resource.ewe.rs/media/category/2022/07/${sanitizedName}.png`;
  };

  const fetchSubcategories = async (categoryId) => {
    setLoadingStates(prev => ({ ...prev, [categoryId]: true }));
    try {
      const response = await fetch(
        `${API_BASE_URL}/ewe/products/categories/${categoryId}`
      );
      if (!response.ok) throw new Error('Failed to fetch subcategories');
      const data = await response.json();
      setSubcategoriesData(prev => ({ ...prev, [categoryId]: data }));
    } catch (error) {
      console.error(`Error fetching subcategories for ${categoryId}:`, error);
    } finally {
      setLoadingStates(prev => ({ ...prev, [categoryId]: false }));
    }
  };

  const toggleCategory = async (categoryId) => {
    const isCurrentlyExpanded = clickedCategoryId[categoryId];
    const isLoading = loadingStates[categoryId];

    // Prevent toggling while loading
    if (isLoading) {
      return;
    }

    // Toggle the expanded state
    setClickedCategoryId(prev => ({
      ...prev,
      [categoryId]: !isCurrentlyExpanded
    }));

    // If we're expanding and don't have data yet, fetch it
    if (!isCurrentlyExpanded && !subcategoriesData[categoryId]) {
      try {
        await fetchSubcategories(categoryId);
      } catch (error) {
        // If fetch fails, collapse the category
        setClickedCategoryId(prev => ({
          ...prev,
          [categoryId]: false
        }));
      }
    }
  };

  const handleTouchStart = (categoryId, e) => {
    e.stopPropagation();
    setTouchData(prev => ({
      ...prev,
      [categoryId]: {
        startTime: Date.now(),
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY
      }
    }));
  };

  const handleTouchEnd = async (categoryId, e) => {
    e.preventDefault();
    e.stopPropagation();

    const touch = touchData[categoryId];
    if (!touch) return;

    const touchDuration = Date.now() - touch.startTime;
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;

    // Calculate distance moved
    const deltaX = Math.abs(touchEndX - touch.startX);
    const deltaY = Math.abs(touchEndY - touch.startY);
    const totalDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Only treat as tap if it's quick and didn't move much
    const isValidTap = touchDuration < 300 && totalDistance < 10;

    if (isValidTap) {
      await toggleCategory(categoryId);
    }

    // Clear touch data
    setTouchData(prev => ({
      ...prev,
      [categoryId]: null
    }));
  };

  const handleClick = async (categoryId, e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if there was a recent touch event to avoid double triggering
    const touch = touchData[categoryId];
    const recentTouch = touch && (Date.now() - touch.startTime < 500);

    if (!recentTouch) {
      await toggleCategory(categoryId);
    }
  };

  const getCategoryChildren = (category) => {
    return subcategoriesData[category.id] || category.children || [];
  };

  const buildCategoryStructure = (categories) => {
    const map = {};
    const roots = [];
    categories.forEach(category => map[category.id] = { ...category, children: [] });
    categories.forEach(category => {
      category.parentId
        ? map[category.parentId]?.children.push(map[category.id])
        : roots.push(map[category.id]);
    });
    return roots;
  };

  const displayCategories = categories[0]?.anMainCategoryKey
    ? buildCategoryStructure(categories)
    : categories;

  return (
    <div className="dropdown-content" ref={dropdownRef} onClick={e => e.stopPropagation()}>
      {displayCategories.map((category) => {
        const children = getCategoryChildren(category);
        const hasChildren = children.length > 0;
        const categoryImage = getCategoryImage(category);
        const isExpanded = !!clickedCategoryId[category.id];
        const isLoading = !!loadingStates[category.id];

        return (
          <div key={category.id} className="category-item-container">
            <div className="category-item">
              <img
                src={categoryImage}
                alt={category.name}
                className="category-image"
                onError={(e) => e.target.style.display = 'none'}
              />

              <span
                className="category-name"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCategoryClick(category);
                  onClose?.();
                }}
              >
                {category.name}
              </span>

              {hasChildren && (
                <span
                  className={`toggle-icon ${isExpanded ? 'expanded' : ''} ${isLoading ? 'loading' : ''}`}
                  onTouchStart={(e) => handleTouchStart(category.id, e)}
                  onTouchEnd={(e) => handleTouchEnd(category.id, e)}
                  onClick={(e) => handleClick(category.id, e)}
                  style={{
                    touchAction: 'manipulation',
                    WebkitTouchCallout: 'none',
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    opacity: isLoading ? 0.6 : 1,
                    pointerEvents: isLoading ? 'none' : 'auto',
                    cursor: 'pointer',
                    padding: '10px',
                    margin: '-10px',
                    minWidth: '44px',
                    minHeight: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {isLoading ? '⏳' : (isExpanded ? '▼' : '▶')}
                </span>
              )}
            </div>

            {hasChildren && (
              <div className={`submenu ${isExpanded ? 'visible' : ''}`}>
                {isLoading ? (
                  <div className="loading-indicator">Loading...</div>
                ) : (
                  <CategoryDropdown
                    categories={children}
                    handleCategoryClick={handleCategoryClick}
                    onClose={onClose}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CategoryDropdown;